import React, { useEffect, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import { FormattedMessage } from "react-intl";
import { useTheme, Box, Button, Slider, Typography } from "@mui/material";
import { useSnackbar } from "../../../../../hooks";
import SimpleModalCompact from "../../../../common/ModalCompact/SimpleModalCompact";

const WorkspaceImageEditor = ({
  onChange,
  onClose,
  targetSize = 100,
}) => {
  const initialState = {
    position: { x: 0.5, y: 0.5 },
    scale: 1
  };

  const canvasSize = 300;

  const theme = useTheme();
  const snackbar = useSnackbar();

  const [editor, setEditor] = useState(null);
  const [editorImage, setEditorImage] = useState(null);
  const [editorPosition, setEditorPosition] = useState(initialState.position);
  const [editorScale, setEditorScale] = useState(initialState.scale);
  const [fileButton, setFileButton] = useState(null);
  const [previousEditorImage, setPreviousEditorImage] = useState(null);

  useEffect(() => {
    fileButton?.click();
  }, [fileButton]);

  const onFileSelected = event => {
    const file = event.target.files[0];
    if (file) {
      setPreviousEditorImage(editorImage);
      setEditorImage(URL.createObjectURL(file));
      setEditorPosition(initialState.position);
      setEditorScale(initialState.scale);
    }
  };

  const onSaveClick = () => {
    const image = editor.getImage();

    var canvas = document.createElement("canvas");

    canvas.width = targetSize
    canvas.height = targetSize

    var ctx = canvas.getContext("2d");

    ctx.drawImage(image, 0, 0, targetSize, targetSize);

    const imageData = canvas.toDataURL();

    onChange?.(imageData);
  };

  const onZoomChange = (event, newValue) => {
    setEditorScale(newValue);
  };

  const removePreviousEditorImage = () => {
    URL.revokeObjectURL(previousEditorImage);
    setPreviousEditorImage(null);
  };

  const restorePreviousEditorImage = () => {
    setEditorImage(previousEditorImage);
    setPreviousEditorImage(null);
  };

  return (
    <SimpleModalCompact
      fullScreen={false}
      open
      onClose={onClose}
      title={
        <FormattedMessage id="admin.workspaces.imageSelector.title" />
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          gap: 1,
          padding: 1,
          paddingTop: 0,
        }}
      >

        <AvatarEditor
          ref={o => setEditor(o)}
          image={editorImage}
          border={35}
          width={canvasSize}
          height={canvasSize}
          position={editorPosition}
          scale={editorScale}
          onLoadFailure={(event) => {
            restorePreviousEditorImage();
            snackbar.error(<FormattedMessage id="avatarUpload.error.invalidImage" />);
          }}
          onLoadSuccess={(imgInfo) => {
            removePreviousEditorImage();
          }}
          onPositionChange={(position) => {
            if (!!editorImage) {
              setEditorPosition(position);
            }
          }}
        />

        <Button
          color="primary"
          component="label"
          size="small"
          variant="contained"
          sx={{
            marginTop: 1,
            minWidth: canvasSize,
          }}
        >
          <FormattedMessage id="admin.workspaces.imageSelector.buttons.select.title" />
          <input
            ref={o => setFileButton(o)}
            type="file"
            accept="image/*"
            onChange={onFileSelected}
            style={{ display: "none" }}
          />
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            maxWidth: canvasSize,
            padding: 0,
            width: "100%",
          }}
        >
          <Typography sx={{ color: !editorImage ? theme.palette.text.disabled : undefined }}>
            <FormattedMessage id="avatarUpload.text.zoom" />:
          </Typography>
          <Slider
            aria-labelledby="continuous-slider"
            disabled={!editorImage}
            max={5}
            min={0.1}
            onChange={onZoomChange}
            step={0.01}
            value={editorScale}
            sx={{
              padding: "20px 0",
            }}
          />
        </Box>

        <Button
          color="primary"
          disabled={!editorImage}
          fullWidth
          onClick={onSaveClick}
          size="small"
          variant="contained"
          sx={{
            maxWidth: canvasSize,
          }}
        >
          <FormattedMessage id="btn.ok" />
        </Button>

      </Box>
    </SimpleModalCompact>
  );
};

export default WorkspaceImageEditor;
